import React, { useState, useMemo } from 'react';
import styled from 'styled-components';

interface Column {
    text: string;
    dataField: string;
    formatter?: (cell: any, row: any, rowIndex: number, formatExtraData?: any) => React.ReactNode;
    sort?: boolean;
    sortCaret?: (order: string) => React.ReactNode;
    filter?: any;
    hidden?: boolean;
    align?: 'left' | 'center' | 'right';
    headerStyle?: () => { width?: string };
}

interface TableXNProps {
    data: any[];
    columns: Column[];
    loading?: boolean;
    labels?: {
        noData?: string;
        showing?: string;
        show?: string;
        to?: string;
        of?: string;
    };
    color?: string;
    noPaginate?: boolean;
    filter?: any;
}

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    margin-top: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: white;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    
    @media (max-width: 768px) {
        min-width: 100%;
        font-size: 0.875rem;
    }
`;

const TableHead = styled.thead<{ color: string }>`
    background-color: ${props => props.color};
    color: white;
    position: sticky;
    top: 0;
    z-index: 10;
`;

const TableHeaderCell = styled.th<{ align?: string; width?: string; sortable?: boolean }>`
    padding: 12px 16px;
    text-align: ${props => props.align || 'left'};
    font-weight: 600;
    white-space: nowrap;
    ${props => props.width && `width: ${props.width};`}
    ${props => props.sortable && `
        cursor: pointer;
        user-select: none;
        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
        }
    `}
    
    @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 0.8rem;
    }
`;

const TableBody = styled.tbody`
    tr:nth-child(even) {
        background-color: #f8f9fa;
    }
    
    tr:hover {
        background-color: #e9ecef;
    }
`;

const TableCell = styled.td<{ align?: string }>`
    padding: 12px 16px;
    text-align: ${props => props.align || 'left'};
    border-bottom: 1px solid #dee2e6;
    
    @media (max-width: 768px) {
        padding: 8px 12px;
    }
`;

const FilterInput = styled.input`
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.875rem;
    margin-top: 4px;
    
    &:focus {
        outline: none;
        border-color: #4a6da7;
    }
`;

const FilterSelect = styled.select`
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.875rem;
    margin-top: 4px;
    background-color: white;
    
    &:focus {
        outline: none;
        border-color: #4a6da7;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #dee2e6;
    flex-wrap: wrap;
    gap: 12px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const PaginationInfo = styled.div`
    font-size: 0.875rem;
    color: #6c757d;
`;

const PaginationControls = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

const PageSizeSelector = styled.select`
    padding: 6px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    
    &:focus {
        outline: none;
        border-color: #4a6da7;
    }
`;

const PageButton = styled.button<{ active?: boolean; disabled?: boolean }>`
    padding: 6px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: ${props => props.active ? '#4a6da7' : 'white'};
    color: ${props => props.active ? 'white' : '#495057'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.disabled ? 0.5 : 1};
    font-size: 0.875rem;
    transition: all 0.2s;
    
    &:hover:not(:disabled) {
        background-color: ${props => props.active ? '#2b436b' : '#e9ecef'};
    }
    
    &:disabled {
        pointer-events: none;
    }
`;

const LoadingOverlay = styled.div`
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 1rem;
`;

const EmptyState = styled.div`
    padding: 40px;
    text-align: center;
    color: #6c757d;
    font-size: 1rem;
`;

const SortIcon = styled.span`
    margin-left: 8px;
    font-size: 0.75rem;
`;

const TableXN: React.FC<TableXNProps> = ({
    data = [],
    columns = [],
    loading = false,
    labels = {},
    color = '#4a6da7',
    noPaginate = false,
    filter,
}: TableXNProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [filters, setFilters] = useState<Record<string, string>>({});

    const defaultLabels = {
        noData: labels.noData || 'Sin datos',
        showing: labels.showing || 'Mostrando',
        show: labels.show || 'Mostrar',
        to: labels.to || 'a',
        of: labels.of || 'de',
    };

    const visibleColumns = useMemo(
        () => columns.filter(col => !col.hidden),
        [columns]
    );

    const filteredData = useMemo(() => {
        if (!data) return [];
        
        return data.filter(row => {
            return Object.keys(filters).every(key => {
                const filterValue = filters[key];
                if (!filterValue) return true;
                
                const cellValue = key.split('.').reduce((obj, k) => obj?.[k], row);
                
                if (cellValue === null || cellValue === undefined) return false;
                
                return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
            });
        });
    }, [data, filters]);

    const sortedData = useMemo(() => {
        if (!sortField) return filteredData;
        
        const sorted = [...filteredData].sort((a, b) => {
            const aValue = sortField.split('.').reduce((obj, k) => obj?.[k], a);
            const bValue = sortField.split('.').reduce((obj, k) => obj?.[k], b);
            
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            
            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
        
        return sorted;
    }, [filteredData, sortField, sortOrder]);

    const totalItems = sortedData.length;
    const totalPages = noPaginate ? 1 : Math.ceil(totalItems / pageSize);
    
    const paginatedData = useMemo(() => {
        if (noPaginate) return sortedData;
        
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, pageSize, noPaginate]);

    const handleSort = (column: Column) => {
        if (!column.sort) return;
        
        if (sortField === column.dataField) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(column.dataField);
            setSortOrder('asc');
        }
    };

    const handleFilterChange = (dataField: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [dataField]: value
        }));
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PageButton
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </PageButton>
            );
        }
        
        return pages;
    };

    const getCellValue = (row: any, dataField: string) => {
        return dataField.split('.').reduce((obj, key) => obj?.[key], row);
    };

    if (loading) {
        return (
            <TableContainer>
                <LoadingOverlay>
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                    <div style={{ marginTop: '12px' }}>Cargando...</div>
                </LoadingOverlay>
            </TableContainer>
        );
    }

    if (!data || data.length === 0) {
        return (
            <TableContainer>
                <EmptyState>{defaultLabels.noData}</EmptyState>
            </TableContainer>
        );
    }

    const startItem = noPaginate ? 1 : (currentPage - 1) * pageSize + 1;
    const endItem = noPaginate ? totalItems : Math.min(currentPage * pageSize, totalItems);

    return (
        <TableContainer>
            <StyledTable>
                <TableHead color={color}>
                    <tr>
                        {visibleColumns.map((column, index) => (
                            <TableHeaderCell
                                key={index}
                                align={column.align}
                                width={column.headerStyle?.().width}
                                sortable={column.sort}
                                onClick={() => handleSort(column)}
                            >
                                <div>
                                    {column.text}
                                    {column.sort && sortField === column.dataField && (
                                        <SortIcon>
                                            {column.sortCaret ? 
                                                column.sortCaret(sortOrder) : 
                                                (sortOrder === 'asc' ? '▲' : '▼')
                                            }
                                        </SortIcon>
                                    )}
                                </div>
                                {filter && column.filter && (
                                    <div onClick={e => e.stopPropagation()}>
                                        {(() => {
                                            const filterProps = column.filter.props || column.filter;
                                            const isSelectFilter = filterProps.options !== undefined;
                                            
                                            if (isSelectFilter) {
                                                return (
                                                    <FilterSelect
                                                        onChange={(e) => handleFilterChange(column.dataField, e.target.value)}
                                                        value={filters[column.dataField] || ''}
                                                    >
                                                        <option value="">{filterProps.placeholder || 'Todos'}</option>
                                                        {Object.entries(filterProps.options).map(([value, label]) => (
                                                            <option key={value} value={value}>{label as string}</option>
                                                        ))}
                                                    </FilterSelect>
                                                );
                                            } else {
                                                return (
                                                    <FilterInput
                                                        type="text"
                                                        placeholder={filterProps.placeholder || ''}
                                                        onChange={(e) => handleFilterChange(column.dataField, e.target.value)}
                                                        value={filters[column.dataField] || ''}
                                                    />
                                                );
                                            }
                                        })()}
                                    </div>
                                )}
                            </TableHeaderCell>
                        ))}
                    </tr>
                </TableHead>
                <TableBody>
                    {paginatedData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {visibleColumns.map((column, colIndex) => {
                                const cellValue = getCellValue(row, column.dataField);
                                const content = column.formatter 
                                    ? column.formatter(cellValue, row, rowIndex)
                                    : cellValue;
                                
                                return (
                                    <TableCell key={colIndex} align={column.align}>
                                        {content}
                                    </TableCell>
                                );
                            })}
                        </tr>
                    ))}
                </TableBody>
            </StyledTable>
            
            {!noPaginate && totalPages > 1 && (
                <PaginationContainer>
                    <PaginationInfo>
                        {defaultLabels.showing} {startItem} {defaultLabels.to} {endItem} {defaultLabels.of} {totalItems}
                    </PaginationInfo>
                    
                    <PaginationControls>
                        <span style={{ fontSize: '0.875rem', color: '#6c757d', marginRight: '8px' }}>
                            {defaultLabels.show}:
                        </span>
                        <PageSizeSelector value={pageSize} onChange={handlePageSizeChange}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </PageSizeSelector>
                        
                        <PageButton
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        >
                            ««
                        </PageButton>
                        
                        <PageButton
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ‹
                        </PageButton>
                        
                        {renderPageNumbers()}
                        
                        <PageButton
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            ›
                        </PageButton>
                        
                        <PageButton
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            »»
                        </PageButton>
                    </PaginationControls>
                </PaginationContainer>
            )}
        </TableContainer>
    );
};

export default TableXN;
